import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import type { StaticImageData } from 'next/image'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useState, useEffect } from 'react'
import { useStore } from 'react-redux'
import Quote from '#/assets/images/car/quote.svg'
import SvgArrowCircle from '#/assets/images/icons/arrow-circle-o-right.svg'
import SvgArrow from '#/assets/images/landing/arrow.svg'
import WfgLogo from '#/assets/images/mls/wfg-logo.png'
import Button from '#/components/Button'
import Carousel from '#/components/Carousel'
import Link from '#/components/Link'
import Meta from '#/components/Meta/meta'
import OverlaySpinner from '#/components/OverlaySpinner'
import Tooltip from '#/components/Tooltip'
import { host } from '#/configs'
import { BREAK_MD, BREAK_SM } from '#/consts'
import { analyticsData } from '#/consts/analytics'
import { avatarSize, companies } from '#/containers/CAR/images'
import testimonials from '#/containers/CAR/testimonial'
import { Props } from '#/containers/CAR/types'
import categories from '#/containers/MapView/components/AnalyticsLayerPanel/categories'
import styles from '#/containers/Products/Studio/styles.module.scss'
import CeoBanner from '#/containers/WFG/components/CeoBanner'
import DiscountBanner from '#/containers/WFG/components/DiscountBanner'
import Intro from '#/containers/WFG/components/Intro'
import Limitations from '#/containers/WFG/components/Limitations'
import { logEvent } from '#/services/analytics'
import { CreateCustomer, SignUp as SignUpAction } from '#/store/actions/user'
import { initialState } from '#/store/reducers/preferences'
import cn from '#/utils/classnames'
import { setIn } from '#/utils/object'
import { state2MapUrl, urlWithQuery } from '#/utils/url'
import imgInmanLogo from '@public/images/car/logos/inman-logo.png'
import imgTopHapAnalytics from '@public/images/car/tophap-analytics.jpg'
import imgTopHapArvCalculator from '@public/images/car/tophap-arv-calculator.jpg'
import imgTopHapBrandedPortal from '@public/images/car/tophap-branded-portal.jpg'
import imgTopHapCMAPlus from '@public/images/car/tophap-cma+.jpg'
import imgTopHapCompare from '@public/images/car/tophap-compare.jpg'
import imgTopHapListings from '@public/images/car/tophap-listings.jpg'
import imgTopHapLotAnalytics from '@public/images/car/tophap-lot-analytics.jpg'
import imgTopHapMarketAnalytics from '@public/images/car/tophap-market-analytics.jpg'
import imgTopHapMarket from '@public/images/car/tophap-market.jpg'
import imgTopHapOffMarketResearch from '@public/images/car/tophap-off-market-research.jpg'
import imgTopHapPermits from '@public/images/car/tophap-permits.jpg'
import imgTopHapPropertyDetail from '@public/images/car/tophap-property-detail.jpg'
import imgTopHapPropertyInsights from '@public/images/car/tophap-property-insights.jpg'
import imgTopHapPropertySearch from '@public/images/car/tophap-property-search.jpg'
import LandingHero from '../Products/Studio/StudioHero'

const CookieConsent = dynamic(() => import('#/components/CookieConsent'), { ssr: false })

const imgBaseUrl = '/images/car'
const mapPage = {
  href: '/map',
  rel: 'nofollow'
}

type MlsInfoProps = {
  [key: string]: {
    analytics?: string
    promoCode: string
    title: string
    titleAbr?: string
    height?: string
    width?: string
  }
}

const mlsInfo: MlsInfoProps = {
  wfg: {
    title: 'WFG National Title',
    promoCode: 'WFG',
    analytics: 'wfg'
  }
}

const CAR: NextPage<Props> = ({
  authenticated /* , currentRole */,
  isMobile,
  asyncStatus
}) => {
  const [isMounted, setMounted] = useState(false)
  const router = useRouter()
  const store = useStore<TopHap.StoreState>()
  const [loading, setLoading] = useState(false)

  const currentDomain = router.pathname.split('/')[1]
  useEffect(() => {
    setMounted(true)

    if (window.rewardful) {
      window.rewardful('source', currentDomain.toUpperCase())
    }
  }, [])

  useEffect(() => {
    if (asyncStatus[SignUpAction.type] === 'request') {
      setLoading(true)
    }
    if (
      asyncStatus[SignUpAction.type] === 'failure' ||
      asyncStatus[CreateCustomer.type] === 'success' ||
      asyncStatus[CreateCustomer.type] === 'failure'
    ) {
      setLoading(false)
    }
  }, [asyncStatus[SignUpAction.type], asyncStatus[CreateCustomer.type]])

  function logSectionClick(name: string) {
    logEvent('select_content', {
      content_type: `${
        mlsInfo[currentDomain].analytics || currentDomain
      }_landing` as 'car_landing' | 'ccar_landing' | 'bayeast_landing',
      item_id: `${name}_section`
    })
  }

  function logCTAClick(name: string) {
    logEvent('select_content', {
      content_type: `${
        mlsInfo[currentDomain].analytics || currentDomain
      }_landing` as 'car_landing' | 'ccar_landing' | 'bayeast_landing',
      item_id: 'click_cta',
      value: `${name}_section`
    })
  }

  function onAnalyticsMetric(metric: TopHap.AnalyticsMetric) {
    const storeState = store.getState()
    let newStore = setIn(storeState, 'preferences.map.descriptive', {
      ...initialState.map.descriptive,
      enabled: true,
      metric
    })
    newStore = setIn(storeState, 'preferences.map.properties.enabled', false)

    if (storeState.ui.viewport.width <= BREAK_SM) {
      newStore = setIn(newStore, 'ui.sider.visible', false)
    }

    router.push(state2MapUrl(newStore))
  }

  type Block = {
    name: string
    image: StaticImageData
    imgAlt: string
    title: React.ReactNode
    badge?: React.ReactNode
    description: string
  }

  function renderInfoSection({
    image,
    imgAlt,
    title,
    description
  }: Omit<Block, 'name'>) {
    return (
      <div className={cn('lg:py-8', styles['th-section'], styles['th-info-section'])}>
        <div className='lg:mx-42 container mx-auto flex max-w-7xl flex-col-reverse items-center justify-between sm:mx-24 sm:px-8 lg:flex-row lg:space-x-24'>
          <div className={cn('max-w-xl mt-8 text-center lg:text-left', styles['th-section-texts'])}>
            <h3 className={cn('mt-8 tracking-normal sm:mt-0', styles['th-section-title'])}>
              {title}
            </h3>
            <p className='text-lg font-light text-gray-500'>{description}</p>
            <div className='mx-auto mt-6 flex items-center text-primary md:mr-auto lg:mx-0'>
              <Link
                {...mapPage}
                onClick={() => logCTAClick('benefits_carousel')}
              >
                {authenticated ? 'Try Now' : 'Get Started'}
                <SvgArrow className={cn('ml-3', styles['th-arrow-icon'])} />
              </Link>
            </div>
          </div>
          <div className='w-full sm:w-7/12'>
            <Image
              alt={imgAlt}
              src={image}
            />
          </div>
        </div>
      </div>
    )
  }

  function renderFeatureBlock({
    name,
    image,
    imgAlt,
    title,
    description,
    badge
  }: Block) {
    return (
      <div className={cn('overflow-hidden border border-gray-100', styles['th-feature-block'])}>
        <Link {...mapPage} onClick={() => logSectionClick(name)}>
          <div className={styles['th-block-img']}>
            <div className='absolute z-10 p-3'>{badge}</div>

            <Image
              alt={imgAlt}
              src={image}
            />
          </div>
        </Link>
        <div className={cn('leading-8', styles['th-block-texts'])}>
          <h3 className={cn('tracking-tighter xs:tracking-normal', styles['th-block-title'])}>
            {title}
          </h3>
          <p className='text-base text-gray-500'>{description}</p>
        </div>
      </div>
    )
  }

  const meta = {
    title: `TopHap | ${mlsInfo[currentDomain].title}`,
    site_name: 'TopHap',
    openGraph: {
      opengraphTitle: `${mlsInfo[currentDomain].title}`,
      opengraphDescription: `Welcome ${
        mlsInfo[currentDomain].title
      } members. Get your TopHap account for free today!`
    },
    description: `Welcome ${
      mlsInfo[currentDomain].title
    } members. Get your TopHap account for free today!`,
    url: host + router.asPath.split('?')[0]
  }

  return (
    <div className={cn(styles['th-landing'], 'th-no-header-space')}>
      {currentDomain && mlsInfo && (
        <Meta meta={meta} />
      )}
      <div className={styles['th-header-back']} />
      <LandingHero />

      <section className='overflow-hidden bg-gray-100 pb-12 pt-1 sm:py-8 md:py-20 lg:py-16'>
        <div className='mx-auto max-w-6xl rounded-lg bg-white py-10 shadow-sm sm:px-8'>
          <div className='px-2 sm:py-5 lg:py-3'>
            <div className='mx-auto flex max-w-5xl flex-col items-center justify-between border-b-2 border-gray-100 sm:border-gray-100 xl:max-w-2xl'>
              <h2 className='max-w-2xl px-4 pb-3 text-center text-2xl font-light tracking-tight text-gray-800 sm:pb-6 md:text-4xl'>
                Welcome WFG Sales Reps!
              </h2>
              <div className='w-full px-5 pb-8 sm:w-8/12 sm:px-12 xl:w-2/3 xl:px-0'>
                <Image
                  alt='wfg'
                  height={751}
                  priority
                  src={WfgLogo}
                  width={1556}
                />
              </div>
              <div className='w-full px-5 pb-8 sm:w-8/12 sm:px-12 xl:w-2/3 xl:px-0'>
                <div className='mx-auto max-w-4xl text-center text-lg font-thin tracking-tight text-gray-500 sm:px-5 '>
                  Hey WFG sales reps, in collaboration
                  with TopHap we bring you the best in
                  class data and analytics to help you
                  serve your clients better!
                </div>
              </div>
            </div>
          </div>
          <div className='flex justify-center py-4'>
            {authenticated ? (
              <Link {...mapPage}>
                <Button
                  className='whitespace-nowrap rounded-full bg-secondary px-8 py-3 text-sm text-white shadow-inner sm:py-4 sm:text-xl'
                  onClick={() => logCTAClick('Intro')}
                >
                  Go to Map
                </Button>
              </Link>
            ) : (
              <Link
                className='flex items-center whitespace-nowrap rounded-full bg-secondary px-8 py-3 text-sm text-white shadow-inner sm:py-4 sm:text-xl'
                {...urlWithQuery(router, { auth: 'signup', pn: 'wfg' })}
                rel='nofollow'
                onClick={() => logCTAClick('Intro')}
              >
                Sign Up!
                <SvgArrowCircle className='ml-2' />
              </Link>
            )}
          </div>
          <Intro />
        </div>
      </section>

      <section className='bg-gray-100 pb-40 pt-1 sm:pb-48'>
        <h1 className='mt-4 text-center text-3xl font-thin tracking-tighter text-gray-900 xs:text-3xl sm:pb-3 sm:tracking-tight md:text-4xl'>
          TopHap
          {' '}
          <strong>Benefits</strong>
        </h1>
        <Carousel
          autoplay
          autoplaySpeed={7500}
          classes={{
            root: cn(styles['th-carousel-container'], styles['th-info-sliders']),
            carousel: styles['th-carousel'],
            nextButton: styles['th-next-button'],
            prevButton: styles['th-prev-button']
          }}
          isMobile={isMobile}
        >
          {renderInfoSection({
            imgAlt: 'Gain Deeper Market Knowledge',
            image: imgTopHapMarket,
            title: (
              <>
                Gain Deeper Market
                <br />
                <strong>Knowledge</strong>
              </>
            ),
            description:
              'Master​ any market by uncovering microtrends and insights that drive property values.'
          })}

          {renderInfoSection({
            imgAlt: 'Supercharge Your Research',
            image: imgTopHapCompare,
            title: (
              <>
                <strong>Supercharge</strong>
                {' '}
                Your Research
              </>
            ),
            description:
              'Utilize​ investor grade real estate analysis tools to save time while researching local and unfamiliar properties and regions.'
          })}

          {renderInfoSection({
            imgAlt: 'Expand Your Territory',
            image: imgTopHapListings,
            title: (
              <>
                <strong>Expand</strong>
                {' '}
                Your Territory
              </>
            ),
            description:
              'Grow​ your business outside of your home territory. Easily analyze any property, neighborhood, city, zip, or county nationally.'
          })}

          {renderInfoSection({
            imgAlt: 'Build Trust & Confidence',
            image: imgTopHapPropertyDetail,
            title: (
              <>
                <strong>Build</strong>
                {' '}
                Trust &
                <br />
                Confidence
              </>
            ),
            description:
              'Harness​ the power of data and AI to build confidence in real estate decisions and advice.'
          })}

          {renderInfoSection({
            imgAlt: 'Your Competitive Advantage',
            image: imgTopHapAnalytics,
            title: (
              <>
                Your Competitive
                <br />
                <strong>Advantage</strong>
              </>
            ),
            description:
              'Access​ the most comprehensive research tools to position yourself as an expert in any market, and deliver the data-driven insights that your clients need.'
          })}
        </Carousel>
      </section>

      <div className='bg-white'>
        <DiscountBanner
          authenticated={authenticated}
          domain={currentDomain}
          logCTAClick={logCTAClick}
          mapPage={mapPage}
          mlsInfo={mlsInfo}
          overlap
          section='benefits'
        />
      </div>

      <Limitations /* renderTryButton={renderTryButton} */ />

      <section className='pb-16 pt-12 text-center'>
        <div className='mx-auto max-w-3xl'>
          <h3 className='text-2xl font-light leading-8 tracking-tighter text-gray-900 sm:tracking-tight md:text-4xl'>
            We’re Here to Help!
          </h3>
          <p className='px-4 pt-3 text-lg font-light text-gray-500 sm:text-xl'>
            TopHap lifts all these limitations by giving you the most
            comprehensive investor-grade Real Estate research tools.
          </p>
        </div>
      </section>

      <CeoBanner />

      <div className='bg-white'>
        <DiscountBanner
          authenticated={authenticated}
          domain={currentDomain}
          logCTAClick={logCTAClick}
          mapPage={mapPage}
          mlsInfo={mlsInfo}
          overlap={false}
          section='agents'
        />
      </div>

      <section className={cn('mb-44', styles['th-section'], styles['th-main-features-section'])}>
        <div className='lg:max-w-8xl mx-auto max-w-md px-2 text-center sm:max-w-3xl sm:px-6 sm:py-4 lg:pt-6'>
          <p className='mt-2 pb-3 text-3xl font-thin leading-8 tracking-tighter text-gray-900 xs:text-3xl sm:tracking-tight md:text-4xl'>
            TopHap
            {' '}
            <strong>Features</strong>
          </p>
        </div>
        <div className={cn('container', styles['th-section-content'])}>
          <div className='mx-auto grid max-w-7xl gap-6 pt-4 sm:grid-cols-2 lg:grid-cols-3 lg:pt-8 xl:grid-cols-3'>
            {renderFeatureBlock({
              name: 'visual_market_analytics',
              imgAlt: 'Visual Market Analytics',
              image: imgTopHapMarketAnalytics,
              title: (
                <>
                  Visual
                  {' '}
                  <strong>Market Analytics</strong>
                </>
              ),
              description:
                'Powerful analytics and map layers enable you to uncover insights about regions, neighborhoods, individual properties and lots in seconds.'
            })}
            {renderFeatureBlock({
              name: 'property_insights',
              imgAlt: 'Property Insights',
              image: imgTopHapPropertyInsights,
              title: (
                <>
                  Property
                  {' '}
                  <strong>Insights</strong>
                </>
              ),
              description:
                'E​xamine attributes and characteristics of any property on or off-market.'
            })}
            {renderFeatureBlock({
              name: 'lot_analytics',
              imgAlt: 'Lot Analytics',
              image: imgTopHapLotAnalytics,
              title: (
                <>
                  <strong>Lot</strong>
                  {' '}
                  Analytics
                </>
              ),
              description:
                'V​isually examine lot usability, topography and elevation without the need to visit the site.'
            })}
            {renderFeatureBlock({
              name: 'statewide_property_search',
              imgAlt: 'Statewide Property Search',
              image: imgTopHapPropertySearch,
              title: (
                <>
                  <strong>Statewide</strong>
                  {' '}
                  Property Search
                </>
              ),
              description:
                'Quickly search all for sale, pending, sold, and rental properties across the entire country.'
            })}
            {renderFeatureBlock({
              name: 'off_market_research',
              imgAlt: 'Off-Market Research',
              image: imgTopHapOffMarketResearch,
              title: (
                <>
                  Off-Market
                  {' '}
                  <strong>Research</strong>
                </>
              ),
              description:
                'Identify properties that are not currently for sale with targeted search criteria.'
            })}
            {renderFeatureBlock({
              name: 'tophap_cma+',
              imgAlt: 'TopHap CMA+',
              image: imgTopHapCMAPlus,
              title: (
                <>
                  TopHap
                  {' '}
                  <strong>CMA+</strong>
                </>
              ),
              description:
                'TopHap’s investor grade comparison engine enables you to see diverging microtrends, compare regions, and evaluate historical changes to markets and properties.'
            })}
            {renderFeatureBlock({
              name: 'arv_calculator',
              imgAlt: 'After Repair Value (ARV) Calculator',
              image: imgTopHapArvCalculator,
              title: (
                <>
                  After Repair Value (ARV)
                  {' '}
                  <strong>Calculator</strong>
                </>
              ),
              description:
                'Reveal the full property improvement potential by adjusting the square footage, room count and property condition.'
            })}
            {renderFeatureBlock({
              name: 'permits',
              imgAlt: 'Permits',
              image: imgTopHapPermits,
              title: <strong>Permits</strong>,
              description:
                'Review full property permit history and analyze full residential building permit activity across the state.'
            })}
            {renderFeatureBlock({
              name: 'permits',
              imgAlt: 'Portal',
              image: imgTopHapBrandedPortal,
              badge: (
                <span className='inline-flex items-center rounded-full bg-primary px-3 py-0.5 text-sm font-medium text-white'>
                  New
                </span>
              ),
              title: (
                <>
                  {' '}
                  <strong> Branded</strong>
                  {' '}
                  Portal
                  {' '}
                </>
              ),
              description:
                'Invite your clients, grow your network and get lead notifications. Guests can access your plan features, at no cost to them.'
            })}
          </div>
        </div>
      </section>

      <section className='bg-gray-50'>
        <DiscountBanner
          authenticated={authenticated}
          domain={currentDomain}
          logCTAClick={logCTAClick}
          mapPage={mapPage}
          mlsInfo={mlsInfo}
          overlap
          section='features'
        />
        <div className={cn('py-12 mx-auto', styles['th-section'], styles['th-analytics-section'])}>
          <h3 className='mt-4 pb-3 pt-2 text-center text-3xl font-light leading-8 tracking-tight text-gray-900 sm:mt-10 sm:text-4xl lg:text-4xl'>
            Our AI Analyzes
            <br />
            Billions of Data Points
          </h3>
          <p className={cn(styles['th-section-description'], 'lg:px-0')}>
            to provide the most extensive real-time real estate insights
          </p>
          <div className='pt-3' />
          <div className={cn('container max-w-6xl mb-8 shadow-lg', styles['th-analytics-metrics'])}>
            {categories.map(category => (
              <div key={category.id} className={styles['th-category']}>
                <div
                  className={styles['th-category-title']}
                  style={{ color: category.color }}
                >
                  <category.Icon />
                  {' '}
                  {category.title}
                </div>
                <div className={styles['th-metrics']}>
                  {category.items.map((e: TopHap.AnalyticsMetric) => (
                    <div
                      key={e}
                      className={styles['th-metric']}
                      onClick={() => onAnalyticsMetric(e)}
                    >
                      <Tooltip
                        tooltip={analyticsData[e].tooltip}
                        trigger='hover'
                        wrap={false}
                      >
                        <span>{analyticsData[e].title}</span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={cn('mx-auto', styles['th-section'], styles['th-company-section'])}>
        <div className='pb-5 pt-10 text-center text-2xl text-gray-800 sm:pt-12'>
          Trusted by industry leaders
        </div>
        <div className={cn('px-2 py-4 xl:px-20', styles['th-section-content'])}>
          <div className={cn('px-3', styles['th-company-logos'])}>
            {companies.map(company => (
              <div key={company.name} className={styles['th-company']}>
                <Image
                  key={company.name}
                  alt={company.name}
                  height={company.height}
                  src={`${imgBaseUrl}/logos/${company.name}.jpg`}
                  width={company.width}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {isMounted && (
        <section className={cn('px-2 py-8 mx-auto max-w-7xl', styles['th-section'], styles['th-testimonial-section'])}>
          <div className='px-8 py-3 pt-6 text-center text-2xl font-light text-gray-800 sm:text-3xl xl:ml-1 xl:px-16 xl:text-left'>
            Testimonials
          </div>
          <Carousel
            autoplay
            autoplaySpeed={7500}
            classes={{
              root: styles['th-carousel-container'],
              container: 'container',
              carousel: cn(styles['th-carousel'], 'lg:container mx-auto'),
              nextButton: styles['th-next-button'],
              prevButton: styles['th-prev-button']
            }}
            isMobile={isMobile}
            responsive={[
              {
                breakpoint: BREAK_MD,
                slidesToShow: 1,
                slidesToScroll: 1
              }
            ]}
            slidesToScroll={2}
            slidesToShow={2}
          >
            {testimonials.map((e, index) => (
              <div key={index} className={cn('px-12', styles['th-testimonial'])}>
                <div className={styles['th-text']}>
                  <Quote className='absolute -left-2 h-3 w-3 text-gray-400' />
                  {e.testimonial}
&nbsp;
                  <Quote className='mb-2 h-3 w-3 rotate-180 text-gray-400' />
                </div>
                <div className={styles['th-person']}>
                  <div className={styles['th-avatar']}>
                    <Image
                      alt={e.name}
                      height={avatarSize}
                      src={e.avatar}
                      width={avatarSize}
                    />
                  </div>
                  <div>
                    <div className={styles['th-name']}>{e.name}</div>
                    {e.license && <div className={styles['th-license']}>{e.license}</div>}
                    <div className={styles['th-job']}>{e.job}</div>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </section>
      )}

      <section className={cn(styles['th-section'], styles['th-inman-testimonial-section'], 'bg-primary')}>
        <div className={cn('px-6', styles['th-section-content'])}>
          <div className='relative mt-4 max-w-4xl px-6 text-center text-lg font-medium md:px-16'>
            <Quote className='absolute left-0 top-0 h-8 w-8 -translate-x-1 -translate-y-2 text-indigo-300 lg:translate-x-5' />

            <p className='relative z-10 text-base font-normal tracking-tight text-white sm:leading-7 md:text-lg'>
              When a user browses TopHap&apos;s tools, they&apos;ll quickly
              understand how even a single street is ultimately its own micro
              market, pulling the curtain back on everything that makes real
              estate worth what it&apos;s worth. My take is that there is no
              better way to understand a market than by understanding how it
              relates to the markets around it.
              {' '}
              <br />
              <br />
              After all, markets touch, and like the properties of thermal
              conduction, values spread through direct contact, and TopHap puts
              you in the middle of all that market energy.
            </p>
            <Quote className='absolute bottom-0 right-0 h-8 w-8 rotate-180 text-indigo-300 sm:-translate-x-12 sm:-translate-y-1 md:-translate-x-10' />
          </div>
          <a
            className={cn('pt-3', styles['th-author'])}
            href='https://www.inman.com/author/craig-rowe/'
            rel='noopener noreferrer'
            target='_blank'
          >
            -Craig C. Rowe
          </a>
          <a
            className='h-12'
            href='https://www.inman.com/2019/11/08/map-based-data-service-tophap-helps-agents-master-their-market/'
            rel='noopener noreferrer'
            target='_blank'
          >
            <div className='mt-6 h-24 w-24'>
              <Image
                alt='inman'
                src={imgInmanLogo}
              />
            </div>
          </a>
        </div>
      </section>

      <section className='flex items-center justify-center bg-header py-6'>
        {authenticated ? (
          <Link {...mapPage}>
            <Button
              className='whitespace-nowrap rounded-full bg-secondary px-8 py-3 text-sm text-white shadow-inner sm:py-4 sm:text-xl'
              onClick={() => logCTAClick('Intro')}
            >
              Go to Map
            </Button>
          </Link>
        ) : (
          <Link
            className='flex items-center whitespace-nowrap rounded-full bg-secondary px-8 py-3 text-sm text-white shadow-inner sm:py-4 sm:text-xl'
            {...urlWithQuery(router, { auth: 'signup', pn: 'wfg' })}
            rel='nofollow'
            onClick={() => logCTAClick('Intro')}
          >
            Sign Up!
            <SvgArrowCircle className='ml-2' />
          </Link>
        )}
      </section>
      <OverlaySpinner absolute visible={loading} />
      <CookieConsent />
    </div>
  )
}

export default CAR
